import {
  AccountCircleRounded,
  CloseRounded,
  ListRounded,
  GridViewRounded
} from "@mui/icons-material"
import {
  Container,
  IconButton,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Pagination,
  List,
  ListItem,
  Box,
  Button,
  Modal
} from "@mui/material"
import React, { useEffect, useState, useCallback, ReactNode, Dispatch, SetStateAction } from "react"
import { useNavigate } from "react-router-dom"
import debounce from 'lodash.debounce'
import Navigation from "../components/navigation"
import { useEmbossers, Embosser } from "../providers/embossers"
import EmbosserCard from "../components/embosserCard"
import { useTheme } from "../providers/theme"
import useWidth from "../hooks/window"
import { makeStyles } from "@mui/styles"
import {
  styles,
  stylesDark,
  Styles
} from "../providers/theme"
import { MultiplePropertiesArea } from "../components/propertyArea"
import { CustomSelect } from "../components/customSelect"
import { FilterPillShape } from "../components/FilterPillShape"
import { useAuth } from "../providers/auth"

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

export default function Embossers() {
  const {
    embossers,
    search,
    setSearch,
    pagesCount,
    currentPage,
    sortBySerialNumber,
    setSortBySerialNumber,
    sortByConnectionDate,
    setSortByConnectionDate,
    filterByManufacturer,
    setFilterByManufacturer,
    filterByFirmwareVersion,
    setFilterByFirmwareVersion,
    filterByCompany,
    setFilterByCompany,
    filterByCountry,
    setFilterByCountry,
    filterByArchitecture,
    setFilterByArchitecture,
    filterByModel,
    setFilterByModel,
    filtersList,
    availableManufacturers,
    availableFirmwareVersions,
    availableDistributors,
    availableCountryCodes,
    availableArchitectures,
    availableModels,
    displayMode,
    setDisplayMode,
    setCurrentPage,
    resultsListLength,
    itemsOnPage,
    setItemsOnPage,
  } = useEmbossers()
  const {currentRole} = useAuth()
  const {mode} = useTheme()
  const width = useWidth()
  const [localSearch, setLocalSearch] = useState<string>(search)
  const [modalContent, setModalContent] = useState<ReactNode>(<></>)
  const filterActive: boolean = filterByManufacturer !== "" || filterByFirmwareVersion !== "" || filterByCompany !== "" || filterByCountry !== "" || filterByArchitecture !== "" || filterByModel !== ""
  const navigate = useNavigate()
  const title = 'Remote Support'
  const itemsOnPageValues: number[] = [15, 30, 45]
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  let classesLight = useStylesLight(styles)
  let classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark
  const [filterAvailable, setFilterAvailable] = useState<boolean>(true)

  useEffect(() => {
    if (availableManufacturers.length <= 1 && availableFirmwareVersions.length <= 1 && availableDistributors.length <= 1 && availableCountryCodes.length <= 1 && availableArchitectures.length <= 1 && availableModels.length <= 1) {
    setFilterAvailable(false)
    } else {
    setFilterAvailable(true)
    }
  }, [availableManufacturers, availableFirmwareVersions, availableDistributors, availableCountryCodes, availableArchitectures, availableModels])
  

  const searchEmbossers = (localSearch: string) => {
    setSearch(localSearch)
  }

  // eslint-disable-next-line
  const debouncedSearchEmbossers = useCallback(debounce(searchEmbossers, 300), [])

  useEffect(() => {
    debouncedSearchEmbossers(localSearch)

    return () => {
      debouncedSearchEmbossers.cancel()
    }
    // eslint-disable-next-line
  }, [localSearch])

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value
    setLocalSearch(newSearch)
  }

  const onChangeItemsOnPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItemsOnPage = parseInt(event.target.value)
    setItemsOnPage(newItemsOnPage)
  }

  return (
    <Navigation
      title={title}
      actions={
        <>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="account-appbar"
            aria-haspopup="true"
            color="inherit"
            edge="end"
            onClick={() => navigate('/account')}
          >
            <AccountCircleRounded />
          </IconButton>
        </>
      }
    >
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex"
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end"
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              my={2}
            >
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              component="h2"
              ml={2}
              my={2}
              sx={{ color: `${mode === "light" ? "#25252580" : "#FFFFFF80"}` }}
            >
              {`${resultsListLength} ${resultsListLength === 1 ? "result" : "results"}`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "auto",
              display: "flex",
              alignItems: "center"
            }}
          >
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "list" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('list')
              }}
            >
              <ListRounded />
            </IconButton>
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "grid" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('grid')
              }}
            >
              <GridViewRounded />
            </IconButton>
          </Box>
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          <TextField
            sx={{
              width: "80%"
            }}
            defaultValue={search ?? ""}
            label="Search"
            size="small"
            onChange={onChangeSearch}
          />
          <Button
            sx={{
              width: "10%",
              paddingInline: "2em !important",
              marginLeft: ".5em",
              border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
              borderRadius: "10px",
              color: `${mode === "light" ? "#252525" : "#FFF"}`,
              "&:hover": {
                border: `1px solid ${mode === "light" ? "#252525" : "#FFF"}`,
                backgroundColor: `${mode === "light" ? "#252525" : "#FFF"}`,
                color: `${mode === "light" ? "#FFF" : "#252525"}`
              }
            }}
            onClick={() => {
              setModalContent(
                <Box
                  sx={{
                    backgroundColor: mode === 'light' ? "#F6F6F6" : "#252525",
                    borderRadius: "10px",
                    boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)",
                    padding: "1em",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                  >
                    Sort By
                  </Typography>
                  <MultiplePropertiesArea
                    topMargin={true}
                    paddingInline={true}
                    property={"Serial Number"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={false}
                        items={[
                          {
                            value: "Default",
                            selected: "default" === sortBySerialNumber.toLowerCase()
                          },
                          {
                            value: "Ascending",
                            selected: "ascending" === sortBySerialNumber.toLowerCase()
                          },
                          {
                            value: "Descending",
                            selected: "descending" === sortBySerialNumber.toLowerCase()
                          }
                        ]}
                        setFunction={setSortBySerialNumber as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                  <MultiplePropertiesArea
                    topMargin={true}
                    paddingInline={true}
                    property={"Connection Date"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={false}
                        items={[
                          {
                            value: "Default",
                            selected: "default" === sortByConnectionDate.toLowerCase()
                          },
                          {
                            value: "Ascending",
                            selected: "ascending" === sortByConnectionDate.toLowerCase()
                          },
                          {
                            value: "Descending",
                            selected: "descending" === sortByConnectionDate.toLowerCase()
                          }
                        ]}
                        setFunction={setSortByConnectionDate as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                </Box>
              )
              setModalOpen(true)
            }}
          >
            Sort
          </Button>
          <Button
            sx={{
              width: "10%",
              paddingInline: "2em !important",
              marginLeft: ".5em",
              border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
              borderRadius: "10px",
              color: `${mode === "light" ? "#252525" : "#FFF"}`,
              "&:hover": {
                border: `1px solid ${mode === "light" ? "#252525" : "#FFF"}`,
                backgroundColor: `${mode === "light" ? "#252525" : "#FFF"}`,
                color: `${mode === "light" ? "#FFF" : "#252525"}`
              }
            }}
            onClick={() => {
              setModalContent(
                <Box
                  sx={{
                    backgroundColor: mode === 'light' ? "#F6F6F6" : "#252525",
                    borderRadius: "10px",
                    boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)",
                    padding: "1em"
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    ml={2}
                  >
                    {filterAvailable ? "Filter By" : "No Filters Available"}
                  </Typography>
                  {
                    currentRole === "Global Administrator" && availableManufacturers.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Manufacturer"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableManufacturers.map((manufacturersIterator) => {
                                return {
                                  value: manufacturersIterator,
                                  selected: filterByManufacturer.toLowerCase().split(';').includes(manufacturersIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByManufacturer as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                  {
                    availableFirmwareVersions.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Firmware Version"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableFirmwareVersions.map((firmwareVersionsIterator) => {
                                return {
                                  value: firmwareVersionsIterator,
                                  selected: filterByFirmwareVersion.toLowerCase().split(';').includes(firmwareVersionsIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByFirmwareVersion as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                  {
                    availableDistributors.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Company"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableDistributors.map((distributorsIterator) => {
                                return {
                                  value: distributorsIterator,
                                  selected: filterByCompany.toLowerCase().split(';').includes(distributorsIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByCompany as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                  {
                    availableCountryCodes.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Country"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableCountryCodes.map((countryCodesIterator) => {
                                return {
                                  value: countryCodesIterator,
                                  selected: filterByCountry.toLowerCase().split(';').includes(countryCodesIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByCountry as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                  {
                    availableArchitectures.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Architecture"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableArchitectures.map(architecturesIterator => {
                                return {
                                  value: architecturesIterator,
                                  selected: filterByArchitecture.toLowerCase().split(';').includes(architecturesIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByArchitecture as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                  {
                    availableModels.length > 1 &&
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Model"}
                        classes={classes}
                        children={
                          <CustomSelect
                            multiple={true}
                            items={
                              availableModels.map(modelsIterator => {
                                return {
                                  value: modelsIterator,
                                  selected: filterByModel.toLowerCase().split(';').includes(modelsIterator.toLowerCase())
                                }
                              })
                            }
                            setFunction={setFilterByModel as Dispatch<SetStateAction<string>>}
                          />
                        }
                      />
                  }
                </Box>
              )
              setModalOpen(true)
            }}
          >
            Filter
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginTop: "1em"
          }}
        >
          {
            filterActive &&
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  marginLeft: ".5em"
                }}
              >
                Filters:
              </Typography>
          }
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              height: "auto"
            }}
          >
            {
              filtersList.map((filter, index) =>
                filter.value !== "" &&
                  <React.Fragment key={index.toString()}>
                  {
                    filter.value.split(';').map((selectedItem) =>
                      <React.Fragment key={selectedItem}>
                        <FilterPillShape
                          itemValue={selectedItem}
                          filter={filter.value}
                          setFilterFunction={filter.setFilterDispatch}
                        />
                      </React.Fragment>
                    )
                  }
                  </React.Fragment>
              )
            }
            {
              filterActive &&
                <Box
                  sx={{
                    borderRadius: "5em",
                    width: "auto",
                    display: "flex",
                    gap: ".25em",
                    fontSize: ".75em",
                    alignItems: "center",
                    paddingInline: ".75em",
                    paddingBlock: ".25em",
                    marginLeft: ".5em",
                    marginBottom: ".5em",
                    cursor: "pointer",
                    color: mode === 'dark' ? "#F6F6F6" : "#252525",
                    transition: "background-color .25s, color .25s",
                    border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
                    "&:hover": {
                      backgroundColor: mode === 'dark' ? "#F6F6F6" : "#252525",
                      color: mode === 'dark' ? "#252525" : "#F6F6F6"
                    }
                  }}
                  onClick={() => {
                    // eslint-disable-next-line
                    filtersList.map(filtersIterator => {
                      filtersIterator.setFilterDispatch("")
                    })
                  }}
                >
                  <p>
                    Clear All
                  </p>
                </Box>
            }
          </div>
        </div>
        {
          displayMode === 'grid' ?
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "center",
                mt: 2,
                mb: 2
              }}
            >
            {
              embossers.length > 0 ?
                embossers.map((embosser: Embosser) =>
                  <Grid
                    item
                    key={embosser.id}
                    alignItems="flex-start"
                  >
                    <EmbosserCard
                      key={embosser.id}
                      embosser={embosser}
                      displayMode={displayMode}
                    />
                  </Grid>
                )
              :
                <Typography
                  gutterBottom
                  variant="h5"
                  component="h2"
                >
                  No match
                </Typography>
            }
            </Grid>
          :
            embossers.length > 0 ?
              <>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    paddingInline: "2em",
                    marginTop: "1.3em",
                    gap: ".5em"
                  }}
                >
                  <p
                    style={{
                      width: "50px",
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Model
                  </p>
                  <p
                    style={{
                      width: "50px",
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Serial
                  </p>
                  <p
                    style={{
                      width: "50px",
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Connected On
                  </p>
                  <Button
                    variant="contained"
                    sx={{
                      width: "100px",
                      maxWidth: "130px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      width: "100px",
                      maxWidth: "130px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                  </Button>
                </Box>
                <List
                  sx={{
                    width: "100%",
                    height: "auto"
                  }}
                >
                  {
                    embossers.length > 0 &&
                    embossers.map((embosser: Embosser) =>
                      <ListItem
                        key={embosser.id}
                        disableGutters
                        sx={{ margin: "0" }}
                      >
                        <EmbosserCard
                          key={embosser.id}
                          embosser={embosser}
                          displayMode={displayMode}
                        />
                      </ListItem>
                    )
                  }
                </List>
              </>
            :
              <Typography
                gutterBottom
                variant="h5"
                component="h2"
              >
                No match
              </Typography>
        }
        {
          pagesCount !== 0 &&
            <Box
              sx={{
                width: "100%",
                height: "auto",
                display: "flex",
                flexDirection: `${width < 1000 ? "column" : "row"}`,
                justifyContent: "space-between",
                alignItems: "center",
                marginBlock: "1em 2.5em"
              }}
            >
              <TextField
                sx={{
                  width: "150px",
                  position: "relative"
                }}
                fullWidth
                value={itemsOnPage}
                label="Items on page"
                size="small"
                select
                onChange={onChangeItemsOnPage}
              >
                {
                  itemsOnPageValues.map((itemsOnPageValue: number, index: number) =>
                    <MenuItem
                      key={index}
                      value={itemsOnPageValue}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&:hover": {
                          backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                        }
                      }}
                    >
                      {itemsOnPageValue}
                    </MenuItem>
                  )
                }
              </TextField>
              <Pagination
                sx={{
                  marginBlock: '1em',
                  position: "relative",
                  right: "50%",
                  display: "flex",
                  width: `${width < 1000 ? "100%" : "auto"}`,
                  justifyContent: "center",
                  transform: "translateX(50%)"
                }}
                count={pagesCount}
                page={currentPage}
                onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                  })
                  setCurrentPage(newPage)
                }}
                size="large"
              />
            </Box>
        }
        {
          modalOpen &&
            <Modal
              open={modalOpen}
              onClose={() => {
                setModalOpen(false)
              }}
            >
              <Box
                className={classes.modalContainer}
                sx={{
                  width: "50%",
                  minWidth: "300px",
                  maxWidth: "700px",
                  paddingTop: "1.5em !important",
                  paddingBottom: "1em"
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "calc(100% - 1em)",
                    overflowY: "auto",
                    marginTop: "1em",
                    borderRadius: "10px",
                    marginRight: "1.5em",
                    padding: "1em"
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    {modalContent}
                  </Box>
                </Box>
                <IconButton
                  className={classes.modalButtonClose}
                  onClick={() => {
                    setModalOpen(false)
                  }}
                >
                  <CloseRounded />
                </IconButton>
              </Box>
            </Modal>
        }
      </Container>
    </Navigation>
  )
}
